/**
 * Gestion des options produit configurables (metafield custom.options).
 * Calcule le surcoût, met à jour le prix affiché et les line item properties.
 */
if (!customElements.get('product-custom-options')) {
  class ProductCustomOptions extends HTMLElement {
    connectedCallback() {
      this.config = this.parseConfig();
      if (!this.config?.length) return;

      this.sectionId = this.dataset.sectionId || null;
      this.isQuickCart = this.dataset.context === 'quick-cart';
      this.hasActivationKeyOption = this.hasActivationKeyProduct();
      this.variantPriceFactors = this.parseVariantPriceFactors();
      this.freeLabel = this.dataset.freeLabel || 'Free';
      this.fromLabel = this.dataset.fromLabel || 'From';
      this.baseVariantPrice = 0;
      this.baseVariantPriceResolved = false;
      this.compareAtPrice = null;

      this.optionItems = [...this.querySelectorAll('.product-custom-options__item')];
      this.priceContainer = this.findPriceContainer();
      this.productCard = this.closest('product-card');

      this.bindQuantityInputs();
      this.bindServiceInputs();
      this.bindFormSubmit();
      this.initBaseVariantPrice();
      this.bindVariantListeners();

      this.updateAll();
      this.prefillServiceFromUrl();
    }

    /**
     * Arrivée depuis la page d'une licence du compte client (#78) : dans le tiroir d'ajout rapide, la clé d'activation
     * de l'adresse (?key=) est remplie et validée, et le domaine de l'adresse (?domain=, son identifiant) coché dans
     * les résultats. Sans paramètre, rien ne change.
     */
    async prefillServiceFromUrl() {
      if (!this.isQuickCart) return;

      const params = new URLSearchParams(window.location.search);
      const key = (params.get('key') || '').trim();
      const item = this.optionItems.find(candidate => candidate.dataset.optionType === 'service');
      const input = item?.querySelector('[data-option-service-value]');
      if (!key || !input) return;

      input.value = key;
      await this.validateServiceOption(item, this.config[parseInt(item.dataset.optionIndex, 10)], key);

      const domain = params.get('domain');
      const checkbox = domain && item.querySelector(`[data-service-domain-id="${CSS.escape(domain)}"]`);
      if (checkbox) {
        checkbox.checked = true;
        this.updateAll();
      }
    }

    /**
     * Synchronise les properties juste avant l'ajout au panier.
     */
    bindFormSubmit() {
      const form = this.closest('form');
      if (!form) return;

      form.addEventListener(
        'submit',
        event => {
          this.updateAll();

          // Un identifiant par ajout : chaque configuration fait sa propre ligne du panier, en quantité 1, que le panier
          // ne permet que de supprimer ou de dupliquer — jamais fusionnée avec une configuration identique (syde/wyomind#94)
          const configInput = this.querySelector('[data-config-id]');
          if (configInput) configInput.value = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

          // Quick cart : bloquer l'ajout si le total (variante + options) est à 0 €
          if (this.isQuickCart && this.getTotalPriceCents() <= 0) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        },
        { capture: true }
      );
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
      this.quickCartVariantHandler?.();
    }

    /**
     * Indique si le produit possède une option activation key (service).
     * @returns {boolean}
     */
    hasActivationKeyProduct() {
      if (this.dataset.hasActivationKey === 'true') return true;

      return this.config?.some(
        option => option?.service || option?.sku === 'activation-key'
      );
    }

    /**
     * Parse la carte des coefficients prix variante (custom.factor).
     * @returns {Object<string, number>}
     */
    parseVariantPriceFactors() {
      const factorsNode =
        this.querySelector('[data-variant-price-factors]') ||
        this.closest('.quick-cart-product')?.querySelector('[data-variant-price-factors]') ||
        this.closest('product-info')?.querySelector('[data-variant-price-factors]');
      if (!factorsNode) return {};

      try {
        return JSON.parse(factorsNode.textContent);
      } catch (error) {
        console.error('product-custom-options: coefficients variante invalides', error);
        return {};
      }
    }

    /**
     * Retourne l'identifiant de la variante sélectionnée dans le formulaire.
     * @returns {number|null}
     */
    getCurrentVariantId() {
      const variantInput = this.isQuickCart
        ? this.productCard?.querySelector('input[name="id"]')
        : this.closest('form')?.querySelector('input[name="id"]');

      const variantId = parseInt(variantInput?.value, 10);
      return Number.isNaN(variantId) ? null : variantId;
    }

    /**
     * Retourne le coefficient custom.factor d'une variante (1 par défaut).
     * @param {number|null} variantId
     * @returns {number}
     */
    getVariantFactor(variantId) {
      if (!this.hasActivationKeyOption || !variantId) return 1;

      const factor = parseFloat(this.variantPriceFactors[String(variantId)]);
      return factor > 0 ? factor : 1;
    }

    /**
     * Arrondit un prix en centimes à l'euro entier le plus proche.
     * @param {number} priceInCents
     * @returns {number}
     */
    roundPriceToWholeEuro(priceInCents) {
      return Math.round(priceInCents / 100) * 100;
    }

    /**
     * Applique le coefficient custom.factor au prix variante (centimes), arrondi à l'euro.
     * @param {number} priceInCents
     * @param {number|null} variantId
     * @returns {number}
     */
    applyVariantPriceFactor(priceInCents, variantId) {
      const factor = this.getVariantFactor(variantId);
      const adjustedPrice = factor === 1 ? priceInCents : Math.round(priceInCents * factor);

      return this.roundPriceToWholeEuro(adjustedPrice);
    }

    /**
     * Met à jour le prix de base et le compare-at depuis une variante Shopify.
     * @param {Object} variant
     */
    setBaseVariantPriceFromVariant(variant) {
      if (!variant) return;

      const variantId = variant.id;
      this.baseVariantPrice = this.applyVariantPriceFactor(variant.price, variantId);
      this.baseVariantPriceResolved = true;

      const rawCompareAt = variant.compare_at_price;
      if (rawCompareAt && rawCompareAt > variant.price) {
        const factoredCompareAt = this.applyVariantPriceFactor(rawCompareAt, variantId);
        this.compareAtPrice =
          factoredCompareAt > this.baseVariantPrice ? factoredCompareAt : null;
      } else {
        this.compareAtPrice = null;
      }
    }

    /**
     * Parse la configuration JSON embarquée dans le snippet.
     * @returns {Array<Object>|null}
     */
    parseConfig() {
      const configNode = this.querySelector('[data-custom-options-config]');
      if (!configNode) return null;

      try {
        return JSON.parse(configNode.textContent);
      } catch (error) {
        console.error('product-custom-options: configuration JSON invalide', error);
        return null;
      }
    }

    /**
     * Localise le conteneur de prix à mettre à jour.
     * @returns {HTMLElement|null}
     */
    findPriceContainer() {
      const selector = this.dataset.priceSelector || '.product__price .price__container';

      const root = this.isQuickCart
        ? this.closest('.quick-cart-product')
        : document.getElementById(`MainProduct-${this.sectionId}`) || this.closest('.product');

      if (!root) {
        return document.querySelector(
          this.isQuickCart ? '.quick-cart-product .product__price .price__container' : selector
        );
      }

      return root.querySelector(selector);
    }

    /**
     * Initialise le prix de base à partir du DOM ou de la variante courante.
     */
    initBaseVariantPrice() {
      if (this.isQuickCart) {
        this.updateBaseVariantPriceFromProductCard();
      }

      // Repli sur data-price si variantsObj indisponible (ex. quick cart à l'ouverture)
      if (!this.baseVariantPriceResolved) {
        this.readBaseVariantPriceFromDom();
      }
    }

    /**
     * Lit le prix de base depuis les data-* du conteneur prix Liquid.
     */
    readBaseVariantPriceFromDom() {
      if (!this.priceContainer?.dataset.price) return;

      const priceFromDom = parseInt(this.priceContainer.dataset.price, 10);
      if (!Number.isNaN(priceFromDom)) {
        this.baseVariantPrice = priceFromDom;
        this.baseVariantPriceResolved = true;
      }

      const compareFromDom = parseInt(this.priceContainer?.dataset.compareAtPrice, 10);
      if (!Number.isNaN(compareFromDom) && compareFromDom > this.baseVariantPrice) {
        this.compareAtPrice = compareFromDom;
      }
    }

    /**
     * Attache les écouteurs sur les steppers de quantité.
     */
    bindQuantityInputs() {
      this.optionItems.forEach(item => {
        const qtyInput = item.querySelector('[data-option-qty]');
        if (!qtyInput) return;

        qtyInput.addEventListener('change', () => {
          this.normalizeQuantityInput(qtyInput);
          this.updateSuffixDisplay(item, qtyInput);
          this.updateAll();
        });

        qtyInput.addEventListener('input', () => {
          this.updateSuffixDisplay(item, qtyInput);
        });
      });
    }

    /**
     * Attache les écouteurs sur les champs service (ex. activation key).
     */
    bindServiceInputs() {
      this.optionItems.forEach(item => {
        if (item.dataset.optionType !== 'service') return;

        const validateButton = item.querySelector('[data-option-service-validate]');
        const valueInput = item.querySelector('[data-option-service-value]');
        if (!validateButton || !valueInput) return;

        const triggerValidation = () => {
          const index = parseInt(item.dataset.optionIndex, 10);
          const option = this.config[index];
          this.validateServiceOption(item, option, valueInput.value.trim());
        };

        validateButton.addEventListener('click', triggerValidation);

        valueInput.addEventListener('keydown', event => {
          if (event.key === 'Enter') {
            event.preventDefault();
            triggerValidation();
          }
        });

        valueInput.addEventListener('input', () => {
          const currentValue = valueInput.value.trim();
          const validatedKey = item.dataset.validatedServiceKey || '';

          // Ne masque la liste que si la clé diffère de celle déjà validée
          if (!currentValue || currentValue !== validatedKey) {
            delete item.dataset.validatedServiceKey;
            this.clearServiceResults(item);
          }

          this.updateAll();
        });
      });
    }

    /**
     * Retourne le code devise actif de la boutique (eur, usd, etc.).
     * @returns {string}
     */
    getActiveCurrencyCode() {
      return (window.Shopify?.currency?.active || 'EUR').toLowerCase();
    }

    /**
     * Retourne le prix d'un domaine en centimes selon la devise active.
     * @param {Object} domain
     * @returns {number}
     */
    getDomainPriceCents(domain) {
      const currency = this.getActiveCurrencyCode();
      const priceKey = currency === 'usd' ? 'usd' : 'eur';
      const amount = parseFloat(domain.price?.[priceKey] ?? domain.price?.eur ?? 0);
      let priceInCents = Math.round(amount * 100);

      // Activation key : le coefficient variante s'applique aussi aux prix domaines API
      if (this.hasActivationKeyOption) {
        priceInCents = this.applyVariantPriceFactor(priceInCents, this.getCurrentVariantId());
      }

      return priceInCents;
    }

    /**
     * Parse la liste des domaines mémorisée après validation du web service.
     * @param {HTMLElement} item
     * @returns {Array<Object>}
     */
    parseServiceDomains(item) {
      try {
        return JSON.parse(item.dataset.serviceDomains || '[]');
      } catch {
        return [];
      }
    }

    /**
     * Retourne les domaines cochés avec leurs données complètes.
     * @param {HTMLElement} item
     * @returns {Array<Object>}
     */
    getSelectedServiceDomains(item) {
      const domains = this.parseServiceDomains(item);
      const selectedIds = new Set(this.getSelectedServiceDomainIds(item));

      return domains.filter(domain => selectedIds.has(String(domain.id)));
    }

    /**
     * Calcule le surcoût total des domaines cochés pour une option service.
     * @param {HTMLElement} item
     * @returns {number}
     */
    calculateServiceOptionPrice(item) {
      return this.getSelectedServiceDomains(item).reduce(
        (total, domain) => total + this.getDomainPriceCents(domain),
        0
      );
    }

    /**
     * Sépare le libellé produit et l'URL depuis la chaîne renvoyée par le web service.
     * @param {string} fullUrl
     * @returns {{title: string, url: string}}
     */
    parseDomainUrl(fullUrl) {
      const trimmed = (fullUrl || '').trim();
      const match = trimmed.match(/^(.+?)\s+(https?:\/\/\S+)$/i);

      if (match) {
        return { title: match[1].trim(), url: match[2].trim() };
      }

      return { title: trimmed, url: '' };
    }

    /**
     * Formate la colonne licence (version + type de licence).
     * @param {Object} domain
     * @returns {string}
     */
    formatServiceLicenseLabel(domain) {
      const version = domain.version || '';
      const license = domain.license || '';

      if (version && license) return `${version} · ${license}`;
      return version || license;
    }

    /**
     * Sérialise une sélection pour une property panier (limite Shopify : 255 caractères).
     * Format : title|domain|license|price|id
     * @param {Object} domain
     * @returns {string}
     */
    serializeServiceItemForCart(domain) {
      const { title, url } = this.parseDomainUrl(domain.url);

      return [
        title,
        url || domain.url || '',
        this.formatServiceLicenseLabel(domain),
        this.formatDecimalPrice(this.getDomainPriceCents(domain)),
        domain.id != null ? String(domain.id) : '',
      ].join('|');
    }

    /**
     * Synchronise les properties indexées _option.{sku}.item.N dans le formulaire.
     * @param {HTMLElement} item
     * @param {Object} option
     * @param {Array<Object>} selectedDomains
     */
    syncServiceItemProperties(item, option, selectedDomains) {
      item.querySelectorAll('[data-option-service-item]').forEach(input => input.remove());

      const sku = option.sku || '';
      selectedDomains.forEach((domain, index) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[_option.${sku}.item.${index}]`;
        input.value = this.serializeServiceItemForCart(domain);
        input.dataset.optionServiceItem = '';
        item.appendChild(input);
      });
    }

    /**
     * Vide la liste des domaines affichée sous le champ service.
     * @param {HTMLElement} item
     */
    clearServiceResults(item) {
      const resultsContainer = item.querySelector('[data-option-service-results]');
      const listNode = item.querySelector('[data-option-service-results-list]');

      if (listNode) listNode.innerHTML = '';
      resultsContainer?.setAttribute('hidden', '');
      delete item.dataset.serviceDomains;
      delete item.dataset.validatedServiceKey;
      this.setServiceMessage(item, null);
    }

    /**
     * Affiche sous le champ service le message d'une réponse sans domaine éligible ('empty') ou d'un appel en
     * échec ('error'), ou l'efface (null) — #96.
     * @param {HTMLElement} item
     * @param {'empty'|'error'|null} kind
     */
    setServiceMessage(item, kind) {
      const node = item.querySelector('[data-option-service-message]');
      if (!node) return;

      const text = kind ? node.dataset[kind === 'empty' ? 'textEmpty' : 'textError'] || '' : '';
      (node.querySelector('[data-option-service-message-text]') || node).textContent = text;
      node.toggleAttribute('hidden', !text);
    }

    /**
     * Affiche les domaines retournés par le web service sous le champ de saisie.
     * @param {HTMLElement} item
     * @param {Array<Object>} domains
     */
    renderServiceResults(item, domains) {
      const resultsContainer = item.querySelector('[data-option-service-results]');
      const listNode = item.querySelector('[data-option-service-results-list]');
      if (!resultsContainer || !listNode) return;

      listNode.innerHTML = '';

      if (!Array.isArray(domains) || domains.length === 0) {
        resultsContainer.setAttribute('hidden', '');
        return;
      }

      const optionIndex = item.dataset.optionIndex;
      const moneyFormat = this.priceContainer?.dataset.moneyFormat;
      item.dataset.serviceDomains = JSON.stringify(domains);

      domains.forEach((domain, index) => {
        const row = document.createElement('li');
        row.className = 'product-custom-options__service-results-row';
        row.dataset.domainId = domain.id;

        const domainCell = document.createElement('span');
        domainCell.className =
          'product-custom-options__service-col product-custom-options__service-col--domain';
        domainCell.textContent = domain.url || '';

        const licenseCell = document.createElement('span');
        licenseCell.className =
          'product-custom-options__service-col product-custom-options__service-col--license';
        licenseCell.textContent = this.formatServiceLicenseLabel(domain);

        const priceCell = document.createElement('span');
        priceCell.className =
          'product-custom-options__service-col product-custom-options__service-col--price';
        const domainPriceCents = this.getDomainPriceCents(domain);

        if (domainPriceCents > 0 && typeof formatPrice === 'function') {
          priceCell.textContent = formatPrice(domainPriceCents, moneyFormat);
        }

        const selectCell = document.createElement('div');
        selectCell.className =
          'product-custom-options__service-col product-custom-options__service-col--select checkbox checkbox--plain';

        const checkboxId = `ProductCustomOptionService-${optionIndex}-${domain.id}-${index}`;
        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.id = checkboxId;
        checkbox.dataset.serviceDomainId = domain.id;
        checkbox.addEventListener('change', () => this.updateAll());

        const label = document.createElement('label');
        label.htmlFor = checkboxId;
        label.className = 'visually-hidden';
        label.textContent = domain.url || String(domain.id);

        selectCell.append(checkbox, label);
        row.append(domainCell, licenseCell, priceCell, selectCell);
        listNode.appendChild(row);
      });

      resultsContainer.removeAttribute('hidden');
    }

    /**
     * Retourne les identifiants des domaines cochés pour une option service.
     * @param {HTMLElement} item
     * @returns {string[]}
     */
    getSelectedServiceDomainIds(item) {
      return [...item.querySelectorAll('[data-service-domain-id]:checked')].map(
        checkbox => checkbox.dataset.serviceDomainId
      );
    }

    /**
     * Remplace les placeholders ${value} dans les données POST du service.
     * @param {Object|null} data
     * @param {string} value
     * @returns {Object}
     */
    resolveServiceData(data, value) {
      if (!data || typeof data !== 'object') return {};

      return Object.entries(data).reduce((resolved, [key, rawValue]) => {
        resolved[key] = String(rawValue).replace(/\$\{value\}/g, value);
        return resolved;
      }, {});
    }

    /**
     * Appelle le web service de validation et affiche la réponse dans la console.
     * @param {HTMLElement} item
     * @param {Object} option
     * @param {string} value
     */
    async validateServiceOption(item, option, value) {
      const service = option.service;
      if (!service?.endpoint || !value) return;

      // Pendant l'appel, le bouton est inactif et montre un indicateur (CSS sur aria-busy) ; le message précédent s'efface
      const validateButton = item.querySelector('[data-option-service-validate]');
      validateButton?.setAttribute('aria-busy', 'true');
      validateButton?.setAttribute('disabled', '');
      this.setServiceMessage(item, null);

      try {
        const payload = this.resolveServiceData(service.data, value);
        const response = await fetch(service.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(payload).toString(),
        });

        const responseText = await response.text();
        let responseData;

        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = responseText;
        }

        console.log('product-custom-options: validation service', {
          sku: option.sku,
          endpoint: service.endpoint,
          status: response.status,
          data: responseData,
        });

        if (response.ok && Array.isArray(responseData)) {
          item.dataset.validatedServiceKey = value;
          this.renderServiceResults(item, responseData);
          // Clé reconnue mais sans domaine éligible : le dire, plutôt qu'une liste vide
          if (responseData.length === 0) this.setServiceMessage(item, 'empty');
        } else {
          this.clearServiceResults(item);
          this.setServiceMessage(item, 'error');
        }

        this.updateAll();
      } catch (error) {
        console.error('product-custom-options: erreur validation service', error);
        this.clearServiceResults(item);
        this.setServiceMessage(item, 'error');
      } finally {
        validateButton?.removeAttribute('aria-busy');
        validateButton?.removeAttribute('disabled');
      }
    }

    /**
     * Écoute les changements de variante (fiche produit et quick cart).
     */
    bindVariantListeners() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && this.sectionId) {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, event => {
          if (event?.data?.sectionId !== this.sectionId) return;

          const variant = event.data.variant;
          if (!variant) return;

          this.setBaseVariantPriceFromVariant(variant);

          // Le DOM prix est remplacé par product-variants.js : ré-appliquer le surcoût
          requestAnimationFrame(() => {
            this.priceContainer = this.findPriceContainer();
            this.updateAll();
          });
        });
      }

      if (this.isQuickCart) {
        this.quickCartVariantHandler = this.onQuickCartVariantChange.bind(this);

        this.productCard?.querySelectorAll('.variant-option-radio-input, .variant-option-select').forEach(input => {
          input.addEventListener('change', this.quickCartVariantHandler);
        });
      }
    }

    /**
     * Met à jour le prix de base depuis le product-card du quick cart.
     */
    onQuickCartVariantChange() {
      requestAnimationFrame(() => {
        this.updateBaseVariantPriceFromProductCard();
        this.priceContainer = this.findPriceContainer();
        this.updateAll();
      });
    }

    /**
     * Lit le prix de la variante sélectionnée dans le product-card parent.
     */
    updateBaseVariantPriceFromProductCard() {
      const variantId = parseInt(this.productCard?.querySelector('input[name="id"]')?.value, 10);
      const selectedVariant = this.productCard?.variantsObj?.find(variant => variant.id == variantId);

      if (!selectedVariant) {
        this.readBaseVariantPriceFromDom();
        return;
      }

      this.setBaseVariantPriceFromVariant(selectedVariant);
    }

    /**
     * Normalise la quantité selon le step et le seuil minimum (qty.default).
     * @param {HTMLInputElement} qtyInput
     */
    normalizeQuantityInput(qtyInput) {
      const step = parseInt(qtyInput.step, 10) || 1;
      const minParsed = parseInt(qtyInput.min, 10);
      const minValue = Number.isNaN(minParsed) ? 0 : minParsed;
      let value = parseInt(qtyInput.value, 10);

      if (Number.isNaN(value) || value < minValue) {
        value = minValue;
      }

      value = Math.round(value / step) * step;

      // L'arrondi au step ne doit pas descendre sous le seuil default
      if (value < minValue) {
        value = minValue;
      }

      qtyInput.value = value;
    }

    /**
     * Affiche le suffixe singulier ou pluriel.
     * @param {HTMLElement} item
     * @param {HTMLInputElement} qtyInput
     */
    updateSuffixDisplay(item, qtyInput) {
      const suffixWrapper = item.querySelector('[data-option-suffix]');
      if (!suffixWrapper) return;

      const qty = parseInt(qtyInput.value, 10) || 0;
      const suffixOne = suffixWrapper.querySelector('[data-suffix-one]');
      const suffixOther = suffixWrapper.querySelector('[data-suffix-other]');

      if (qty === 1) {
        suffixOne?.removeAttribute('hidden');
        suffixOther?.setAttribute('hidden', '');
      } else {
        suffixOne?.setAttribute('hidden', '');
        suffixOther?.removeAttribute('hidden');
      }
    }

    /**
     * Calcule le surcoût d'une option.
     * @param {Object} option
     * @param {number} qty
     * @param {number} basePrice Prix variante en centimes
     * @returns {number}
     */
    calculateOptionPrice(option, qty, basePrice) {
      const freeQty = parseInt(option.qty?.free, 10) || 0;
      const forQty = parseInt(option.price?.for_qty, 10) || 1;
      const billableQty = Math.max(0, qty - freeQty);

      if (billableQty <= 0 || forQty <= 0) return 0;

      const blocks = billableQty / forQty;
      if (!Number.isInteger(blocks)) return 0;

      if (option.price?.is_fixed) {
        const amount = parseFloat(option.price.amount) || 0;
        return blocks * amount * 100;
      }

      const percentageOff = parseFloat(option.price?.percentage_off) || 0;
      const ceilAmount = parseFloat(option.price?.ceil) || 0;
      let unitPrice = basePrice * (100 - percentageOff) / 100;

      // Arrondi au multiple de ceil € supérieur (ex. ceil: 5 => 61,75 € devient 65 €)
      unitPrice = this.applyPriceCeil(unitPrice, ceilAmount);

      return blocks * unitPrice;
    }

    /**
     * Arrondit un prix (centimes) au multiple supérieur de ceil euros.
     * @param {number} priceInCents
     * @param {number} ceilInEuros Pas d'arrondi si absent ou <= 0
     * @returns {number}
     */
    applyPriceCeil(priceInCents, ceilInEuros) {
      if (!ceilInEuros || ceilInEuros <= 0) return priceInCents;

      const step = ceilInEuros * 100;
      return Math.ceil(priceInCents / step) * step;
    }

    /**
     * Formate la valeur de la line item property.
     * @param {number} qty
     * @param {Object|null} suffix
     * @returns {string}
     */
    formatPropertyValue(qty, suffix) {
      if (!suffix?.one) return String(qty);
      const suffixLabel = qty === 1 ? suffix.one : suffix.other;
      return `${qty} ${suffixLabel}`;
    }

    /**
     * Retourne le prix total affiché (variante + options) en centimes.
     * @returns {number}
     */
    getTotalPriceCents() {
      if (!this.baseVariantPriceResolved) {
        this.readBaseVariantPriceFromDom();
      }

      const basePrice = this.baseVariantPriceResolved ? this.baseVariantPrice : 0;

      return basePrice + this.getOptionsTotal();
    }

    /**
     * Active ou désactive le bouton d'ajout au panier du quick cart selon le total.
     */
    updateQuickCartSubmitState() {
      if (!this.isQuickCart) return;

      const submitButton = this.closest('form')?.querySelector('button[type="submit"]');
      if (!submitButton) return;

      const isZeroTotal = this.getTotalPriceCents() <= 0;
      const isProductUnavailable = submitButton.dataset.productUnavailable === 'true';

      if (isZeroTotal || isProductUnavailable) {
        submitButton.setAttribute('disabled', '');
        return;
      }

      submitButton.removeAttribute('disabled');
    }

    /**
     * Retourne le total des surcoûts options en centimes.
     * @returns {number}
     */
    getOptionsTotal() {
      return this.optionItems.reduce((total, item) => {
        if (item.dataset.optionType === 'service') {
          return total + this.calculateServiceOptionPrice(item);
        }

        const index = parseInt(item.dataset.optionIndex, 10);
        const option = this.config[index];
        const qty = parseInt(item.querySelector('[data-option-qty]')?.value, 10) || 0;

        return total + this.calculateOptionPrice(option, qty, this.baseVariantPrice);
      }, 0);
    }

    /**
     * Rafraîchit les prix affichés dans la liste des domaines (changement de variante / coefficient).
     */
    updateServiceResultPrices() {
      if (!this.hasActivationKeyOption) return;

      const moneyFormat = this.priceContainer?.dataset.moneyFormat;

      this.optionItems.forEach(item => {
        if (item.dataset.optionType !== 'service') return;

        const domains = this.parseServiceDomains(item);
        if (!domains.length) return;

        const rows = item.querySelectorAll('.product-custom-options__service-results-row');

        rows.forEach((row, index) => {
          const domain = domains[index];
          if (!domain) return;

          const priceCell = row.querySelector('.product-custom-options__service-col--price');
          if (!priceCell) return;

          const domainPriceCents = this.getDomainPriceCents(domain);

          if (domainPriceCents > 0 && typeof formatPrice === 'function') {
            priceCell.textContent = formatPrice(domainPriceCents, moneyFormat);
          } else {
            priceCell.textContent = '';
          }
        });
      });
    }

    /**
     * Met à jour properties, prix par option et prix total affiché.
     */
    updateAll() {
      this.updateProperties();
      this.updateOptionPrices();
      this.updateServiceResultPrices();
      this.updateDisplayedPrice();
      this.updateQuickCartSubmitState();
    }

    /**
     * Formate un montant centimes en décimal (ex. 6500 => "65.00").
     * @param {number} priceInCents
     * @returns {string}
     */
    formatDecimalPrice(priceInCents) {
      return (priceInCents / 100).toFixed(2);
    }

    /**
     * Construit les entrées items pour une option service dans _options.
     * @param {Array<Object>} selectedDomains
     * @returns {Array<{product: string, domain: string, license: string}>}
     */
    buildServiceOptionItems(selectedDomains) {
      return selectedDomains.map(domain => {
        const { title, url } = this.parseDomainUrl(domain.url);

        return {
          product: title,
          domain: url || domain.url || '',
          license: this.formatServiceLicenseLabel(domain),
        };
      });
    }

    /**
     * Formate le prix total en euros pour _options (ex. 35500 => "355").
     * @param {number} priceInCents
     * @returns {string}
     */
    formatOptionsJsonPrice(priceInCents) {
      return String(Math.round(priceInCents / 100));
    }

    /**
     * Construit le JSON des options sélectionnées pour la property _options.
     * Options service (activation key) : sku, qty, price et items (product, domain, license).
     * @returns {string}
     */
    buildOptionsJson() {
      const selectedOptions = this.optionItems.reduce((entries, item) => {
        const index = parseInt(item.dataset.optionIndex, 10);
        const option = this.config[index];

        if (item.dataset.optionType === 'service') {
          const selectedDomains = this.getSelectedServiceDomains(item);
          if (!selectedDomains.length) return entries;

          const optionPriceCents = this.calculateServiceOptionPrice(item);

          entries.push({
            sku: option.sku,
            qty: String(selectedDomains.length),
            price: this.formatOptionsJsonPrice(optionPriceCents),
            items: this.buildServiceOptionItems(selectedDomains),
          });

          return entries;
        }

        const qty = parseInt(item.querySelector('[data-option-qty]')?.value, 10) || 0;

        if (qty <= 0) return entries;

        const optionPriceCents = this.calculateOptionPrice(option, qty, this.baseVariantPrice);

        entries.push({
          sku: option.sku,
          qty,
          price: parseFloat(this.formatDecimalPrice(optionPriceCents)),
        });

        return entries;
      }, []);

      return JSON.stringify(selectedOptions);
    }

    /**
     * Met à jour les champs hidden properties du formulaire.
     */
    updateProperties() {
      this.optionItems.forEach(item => {
        const index = parseInt(item.dataset.optionIndex, 10);
        const option = this.config[index];
        const propertyInput = item.querySelector('[data-option-property]');
        const codeInput = item.querySelector('[data-option-hidden-code]');

        // Options service (ex. activation key) : clé + domaines sélectionnés + surcoût
        if (item.dataset.optionType === 'service') {
          if (!propertyInput) return;

          const serviceValue = item.querySelector('[data-option-service-value]')?.value.trim() || '';
          const selectedDomains = this.getSelectedServiceDomains(item);
          const qtyHiddenInput = item.querySelector('[data-option-hidden-qty]');

          const hasServiceSelection = serviceValue && selectedDomains.length > 0;

          if (hasServiceSelection) {
            propertyInput.value = serviceValue;
            propertyInput.removeAttribute('disabled');

            if (qtyHiddenInput) {
              qtyHiddenInput.value = String(selectedDomains.length);
              qtyHiddenInput.removeAttribute('disabled');
            }

            this.syncServiceItemProperties(item, option, selectedDomains);

            if (codeInput) {
              codeInput.value = option.sku || '';
              codeInput.removeAttribute('disabled');
            }
          } else {
            propertyInput.value = '';
            propertyInput.setAttribute('disabled', '');

            if (qtyHiddenInput) {
              qtyHiddenInput.value = '';
              qtyHiddenInput.setAttribute('disabled', '');
            }

            this.syncServiceItemProperties(item, option, []);

            if (codeInput) {
              codeInput.value = option.sku || '';
              codeInput.setAttribute('disabled', '');
            }
          }

          return;
        }

        const qtyInput = item.querySelector('[data-option-qty]');
        const costInput = item.querySelector('[data-option-hidden-cost]');
        const qtyHiddenInput = item.querySelector('[data-option-hidden-qty]');

        if (!qtyInput || !propertyInput) return;

        const qty = parseInt(qtyInput.value, 10) || 0;
        const optionPriceCents = this.calculateOptionPrice(option, qty, this.baseVariantPrice);

        if (qty > 0) {
          propertyInput.value = this.formatPropertyValue(qty, option.suffix || null);
          propertyInput.removeAttribute('disabled');

          if (costInput) {
            costInput.value = this.formatDecimalPrice(optionPriceCents);
            costInput.removeAttribute('disabled');
          }
          if (qtyHiddenInput) {
            qtyHiddenInput.value = String(qty);
            qtyHiddenInput.removeAttribute('disabled');
          }
          if (codeInput) {
            codeInput.value = option.sku || '';
            codeInput.removeAttribute('disabled');
          }
        } else {
          propertyInput.value = '';
          propertyInput.setAttribute('disabled', '');

          [costInput, qtyHiddenInput, codeInput].forEach(input => {
            if (!input) return;
            input.value = input.hasAttribute('data-option-hidden-code') ? option.sku || '' : '';
            input.setAttribute('disabled', '');
          });
        }
      });

      const optionsJsonInput = this.querySelector('[data-options-json]');
      if (optionsJsonInput) {
        const optionsJson = this.buildOptionsJson();
        const hasSelectedOptions = optionsJson !== '[]';

        if (hasSelectedOptions) {
          optionsJsonInput.value = optionsJson;
          optionsJsonInput.removeAttribute('disabled');
        } else {
          optionsJsonInput.value = '';
          optionsJsonInput.setAttribute('disabled', '');
        }
      }
    }

    /**
     * Indique si la quantité sélectionnée est entièrement couverte par le quota gratuit.
     * @param {Object} option
     * @param {number} qty
     * @returns {boolean}
     */
    isOptionFullyFree(option, qty) {
      const freeQty = parseInt(option.qty?.free, 10) || 0;
      return freeQty > 0 && qty > 0 && qty <= freeQty;
    }

    /**
     * Affiche le sous-total de chaque option.
     */
    updateOptionPrices() {
      if (!this.priceContainer) return;

      const moneyFormat = this.priceContainer.dataset.moneyFormat;

      this.optionItems.forEach(item => {
        if (item.dataset.optionType === 'service') return;

        const index = parseInt(item.dataset.optionIndex, 10);
        const option = this.config[index];
        const qty = parseInt(item.querySelector('[data-option-qty]')?.value, 10) || 0;
        const optionPriceNode = item.querySelector('[data-option-price]');
        const optionPrice = this.calculateOptionPrice(option, qty, this.baseVariantPrice);

        if (!optionPriceNode) return;

        optionPriceNode.classList.remove('product-custom-options__option-price--free', 'product-custom-options__option-price--from');

        // Quantité à zéro : le prix de la première quantité payante, en retrait (« From 50,00 € »)
        const fromPrice =
          qty === 0
            ? this.calculateOptionPrice(
                option,
                (parseInt(option.qty?.free, 10) || 0) + (parseInt(option.price?.for_qty, 10) || 1),
                this.baseVariantPrice
              )
            : 0;

        if (optionPrice > 0 && typeof formatPrice === 'function') {
          optionPriceNode.textContent = `+${formatPrice(optionPrice, moneyFormat)}`;
          optionPriceNode.classList.remove('hidden');
        } else if (this.isOptionFullyFree(option, qty)) {
          optionPriceNode.textContent = this.freeLabel;
          optionPriceNode.classList.add('product-custom-options__option-price--free');
          optionPriceNode.classList.remove('hidden');
        } else if (fromPrice > 0 && typeof formatPrice === 'function') {
          optionPriceNode.textContent = `${this.fromLabel} ${formatPrice(fromPrice, moneyFormat)}`;
          optionPriceNode.classList.add('product-custom-options__option-price--from');
          optionPriceNode.classList.remove('hidden');
        } else {
          optionPriceNode.textContent = '';
          optionPriceNode.classList.add('hidden');
        }
      });
    }

    /**
     * Met à jour le prix total affiché (variante + options).
     */
    updateDisplayedPrice() {
      if (!this.priceContainer || typeof formatPrice !== 'function') return;

      // Ne pas écraser le prix Liquid si le prix de base n'est pas encore résolu
      if (!this.baseVariantPriceResolved) {
        this.readBaseVariantPriceFromDom();
      }

      const optionsTotal = this.getOptionsTotal();

      // Variante à 0 € : afficher quand même le surcoût des options (ex. activation key)
      if (!this.baseVariantPriceResolved && optionsTotal === 0) return;

      const finalPrice = this.baseVariantPrice + optionsTotal;
      const comparePrice =
        this.compareAtPrice && this.compareAtPrice > this.baseVariantPrice
          ? this.compareAtPrice + optionsTotal
          : null;

      // Variante à 0 € : ne pas afficher 0 € (uniquement le surcoût options si présent)
      if (finalPrice <= 0) {
        this.priceContainer.innerHTML = '';
        return;
      }

      const moneyFormat = this.priceContainer.dataset.moneyFormat;
      const labelPriceRegular = this.priceContainer.dataset.labelPriceRegular;
      const labelPriceSale = this.priceContainer.dataset.labelPriceSale;

      let priceHtml = '';

      if (comparePrice && comparePrice > finalPrice) {
        priceHtml = `
          <div class="price__sale">
            <div class="price__sale-inner">
              <span class="visually-hidden">${labelPriceSale || ''}</span>
              <s>${formatPrice(comparePrice, moneyFormat)}</s>
              <ins>
                <span class="visually-hidden">${labelPriceRegular || ''}</span>
                ${formatPrice(finalPrice, moneyFormat)}
              </ins>
            </div>
          </div>
        `;
      } else {
        priceHtml = `
          <div class="price__regular">
            <span class="visually-hidden">${labelPriceRegular || ''}</span>
            <span>${formatPrice(finalPrice, moneyFormat)}</span>
          </div>
        `;
      }

      this.priceContainer.innerHTML = priceHtml;
    }
  }

  customElements.define('product-custom-options', ProductCustomOptions);
}
